import { atom } from 'nanostores';
import type { ProviderInfo } from '~/types/model';

/**
 * In the client-only SPA, provider data is fetched from the API
 * rather than bundled from the LLMManager. These stores are populated
 * by calling `loadProviderList()` which hits the /api/models endpoint.
 */

export const providerListStore = atom<ProviderInfo[]>([]);
export const defaultProviderStore = atom<ProviderInfo | undefined>(undefined);
export const providerBaseUrlEnvKeysStore = atom<Record<string, { baseUrlKey?: string; apiTokenKey?: string }>>({});

/** Convenience getters matching the old constant exports */
export function getProviderList(): ProviderInfo[] {
  return providerListStore.get();
}

export function getDefaultProvider(): ProviderInfo | undefined {
  return defaultProviderStore.get();
}

export function getProviderBaseUrlEnvKeys(): Record<string, { baseUrlKey?: string; apiTokenKey?: string }> {
  return providerBaseUrlEnvKeysStore.get();
}

/** Fetch provider/model data from the API and populate the stores */
export async function loadProviderList(): Promise<void> {
  try {
    const response = await fetch('/api/models');

    if (!response.ok) {
      console.error('Failed to fetch provider list:', response.statusText);
      return;
    }

    const data = (await response.json()) as {
      modelList: Array<{
        name: string;
        label: string;
        provider: string;
        maxTokenAllowed: number;
      }>;
      providers: ProviderInfo[];
      defaultProvider?: ProviderInfo;
      providerBaseUrlEnvKeys?: Record<string, { baseUrlKey?: string; apiTokenKey?: string }>;
    };

    if (data.providers) {
      providerListStore.set(data.providers);

      /*
       * Sync providersStore so all fetched providers are enabled by default.
       * Lazy import to avoid circular dependency (settings.ts → provider-list.ts).
       */
      import('~/lib/stores/settings').then(({ syncProvidersFromApi }) => {
        syncProvidersFromApi(data.providers);
      });
    }

    if (data.defaultProvider) {
      defaultProviderStore.set(data.defaultProvider);
    }

    if (data.providerBaseUrlEnvKeys) {
      providerBaseUrlEnvKeysStore.set(data.providerBaseUrlEnvKeys);
    }
  } catch (error) {
    console.error('Error loading provider list from API:', error);
  }
}

/**
 * Re-exported as constants for backward compatibility.
 * These are empty defaults that get populated once loadProviderList() resolves.
 * Components should prefer using the store subscriptions or getProviderList()/getDefaultProvider().
 */
export const PROVIDER_LIST: ProviderInfo[] = [];
export const DEFAULT_PROVIDER: ProviderInfo | undefined = undefined;
